/**
 * "How approving works" (29 Oct): the one-minute guide for approvers.
 *
 * Rusty: approvers must be able to start at go-live without meetings, "idiot
 * proof". So one short page: where an approval reaches you (Teams, email,
 * My approvals), the three steps (read, decide, done), what the Teams card
 * looks like, and a few "good to know" lines. Linked from the first Teams
 * cards an approver gets, the bot's welcome card, My approvals and the
 * announcement email. No SLAs, no phone numbers (user-facing content rules).
 */

import {
  Check,
  CircleCheck,
  Eye,
  Icon,
  Mail,
  MessageSquare,
  SquareCheck,
  ThumbsDown,
  ThumbsUp,
} from "@norican/icons";
import { Button, Typography } from "@norican/react";
import { Link } from "react-router";

import { PageContainer, SectionCard } from "@/components/common";
import { useDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";

export const HOW_APPROVING_WORKS_PATH = "/approvals/how-it-works";

/** The Teams card is English for everyone (phase 1), so the example is too. */
const EXAMPLE = {
  heading: "Approval needed: 365FO Accounts payable clerk",
  facts: [
    ["For", "Anna Example"],
    ["Access", "Accounts payable clerk (add)"],
    ["Asked by", "Her manager"],
  ],
  why: "She takes over supplier invoices in Finance from Monday.",
} as const;

export function HowApprovingWorksPage() {
  const { t } = useT("tickets");
  useDocumentTitle(t("access.howApproving.title"));

  const where = [
    { icon: MessageSquare, key: "teams" },
    { icon: Mail, key: "email" },
    { icon: SquareCheck, key: "helpdesk" },
  ] as const;
  const steps = [
    { icon: Eye, key: "step1" },
    { icon: ThumbsUp, key: "step2" },
    { icon: CircleCheck, key: "step3" },
  ] as const;
  const good = ["good1", "good2", "good3", "good4"] as const;

  return (
    <PageContainer
      title={t("access.howApproving.title")}
      subtitle={t("access.howApproving.subtitle")}
      width="narrow"
    >
      <div className="flex flex-col gap-6">
        <SectionCard title={t("access.howApproving.stepsTitle")}>
          <ol className="m-0 grid list-none gap-4 p-0 sm:grid-cols-3">
            {steps.map(({ icon, key }, index) => (
              <li key={key} className="flex flex-col items-center gap-2 text-center">
                <span className="bg-accent-subtle flex size-12 items-center justify-center rounded-full">
                  <Icon icon={icon} size="md" tone="accent" />
                </span>
                <Typography as="span" variant="body" className="font-semibold">
                  {index + 1}. {t(`access.howApproving.${key}Title`)}
                </Typography>
                <Typography as="span" variant="body-sm">
                  {t(`access.howApproving.${key}Text`)}
                </Typography>
              </li>
            ))}
          </ol>
        </SectionCard>

        <SectionCard
          title={t("access.howApproving.whereTitle")}
          description={t("access.howApproving.whereAny")}
        >
          <ul className="m-0 grid list-none gap-3 p-0 sm:grid-cols-3">
            {where.map(({ icon, key }) => (
              <li key={key} className="flex items-start gap-3 rounded-md border p-3">
                <Icon icon={icon} size="md" tone="accent" />
                <span className="flex flex-col gap-1">
                  <Typography as="span" variant="body" className="font-semibold">
                    {t(`access.howApproving.${key}Title`)}
                  </Typography>
                  <Typography as="span" variant="body-sm">
                    {t(`access.howApproving.${key}Text`)}
                  </Typography>
                </span>
              </li>
            ))}
          </ul>
        </SectionCard>

        <SectionCard title={t("access.howApproving.exampleTitle")}>
          <div
            aria-label={t("access.howApproving.exampleTitle")}
            className="flex max-w-md flex-col gap-3 rounded-md border p-4 shadow-sm"
          >
            <Typography as="span" variant="body" className="font-semibold">
              {EXAMPLE.heading}
            </Typography>
            <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              {EXAMPLE.facts.map(([label, value]) => (
                <div key={label} className="contents">
                  <dt className="font-semibold">{label}</dt>
                  <dd className="m-0">{value}</dd>
                </div>
              ))}
            </dl>
            <Typography as="span" variant="body-sm">
              <strong>Why:</strong> {EXAMPLE.why}
            </Typography>
            <div className="flex flex-wrap gap-2" aria-hidden>
              <span className="bg-success-subtle inline-flex items-center gap-1 rounded-md px-3 py-1 text-sm font-semibold">
                <Icon icon={Check} size="xs" /> Approve
              </span>
              <span className="bg-danger-subtle inline-flex items-center gap-1 rounded-md px-3 py-1 text-sm font-semibold">
                <Icon icon={ThumbsDown} size="xs" /> Reject…
              </span>
              <span className="inline-flex items-center rounded-md border px-3 py-1 text-sm">
                Open in the HelpDesk
              </span>
            </div>
          </div>
          <Typography as="p" variant="body-sm" className="mt-3">
            {t("access.howApproving.exampleHint")}
          </Typography>
        </SectionCard>

        <SectionCard title={t("access.howApproving.goodTitle")}>
          <ul className="m-0 flex flex-col gap-2 pl-5">
            {good.map((key) => (
              <li key={key}>
                <Typography as="span" variant="body-sm">
                  {t(`access.howApproving.${key}`)}
                </Typography>
              </li>
            ))}
          </ul>
        </SectionCard>

        <div className="flex flex-wrap gap-2">
          <Button render={<Link to="/approvals" />}>{t("access.howApproving.cta")}</Button>
          <Button variant="ghost" render={<Link to="/ask" />}>
            {t("access.howApproving.ctaChat")}
          </Button>
        </div>
      </div>
    </PageContainer>
  );
}
